import {
  Card,
  Typography,
  Stack,
  Box
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import { Routes } from "../../routes/RouteConstants";
import CustomButton from "../../components/CustomButton";
import { useNavigate } from "react-router-dom";
import { storage } from "../../services/storageService";
import { StorageKeys } from "../../services/constants/StorageKeys";
import { useTenant } from "../../contexts/TenantContext";
import { tint } from "../../utils/textStyles";
import { formatPrice } from "../../utils/priceUtils";
import { isDarkTheme } from "../../utils/themeTraits";
import { cardSurfaceSx } from "../../utils/cardSurfaces";

// Every card is laid out the way a phone shows it — photo on top, then the name, the facts and
// the button — at every width. A wide screen gets more of them side by side (the grid is in
// SupplyPage), never a wider card.
// 3:2, the shape of herevr's photos, which carry their text at the bottom edge: a fixed height
// cropped it off. A square photo loses its bottom third instead.
const SUPPLY_IMAGE_RATIO = "3 / 2";

// Stands in for the photo of a supply that has none, on a list where others do, so the cards
// of one row still line up. Grey on paper, a faint white veil on the dark theme.
const ImagePlaceholder = ({ isDark }) => (
  <Box
    data-testid="supply-image-placeholder"
    role="img"
    aria-label="Nincs kép"
    sx={{
      aspectRatio: SUPPLY_IMAGE_RATIO,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: isDark ? tint("#fff", 0.06) : "#eeeeee",
      color: isDark ? tint("#fff", 0.3) : "#bdbdbd",
    }}
  >
    <ImageNotSupportedOutlinedIcon sx={{ fontSize: 56 }} />
  </Box>
);

/**
 * `showImageSlot`: whether a card without a photo keeps the photo's place, with a placeholder
 * in it. SupplyPage turns it on when at least one supply of the tenant has a photo; a tenant
 * with no photos at all gets no grey boxes.
 */
const SupplyItem = ({ supply, showImageSlot = false }) => {
  const { displayConfig } = useTenant();
  const navigate = useNavigate();
  const isDark = isDarkTheme(displayConfig);
  const surfaceSx = cardSurfaceSx(displayConfig);

  const hasImage = !!supply?.imageUrl;
  const hasDescription = !!supply?.shortDescription;

  const handleClick = () => {
    storage.set(StorageKeys.SELECTED_SUPPLY, supply);
    navigate(Routes.BOOKING);
  };

  return (
    <Card
      data-testid="supply-card"
      data-supply-id={supply?.id}
      sx={{
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        backgroundColor: displayConfig?.sectionBackground,
        color: displayConfig?.color,
        borderRadius: 2,
        overflow: "hidden",
        transition: "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
        ...surfaceSx,
        "&:hover": { ...surfaceSx["&:hover"], transform: "translateY(-2px)" },
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
          "&:hover": { transform: "none" },
        },
      }}
    >
      {/* Image */}
      {hasImage && (
        <Box
          component="img"
          src={supply.imageUrl}
          alt={supply.name}
          sx={{
            width: "100%",
            aspectRatio: SUPPLY_IMAGE_RATIO,
            objectFit: "cover",
            objectPosition: "top",
            flexShrink: 0,
            display: "block",
          }}
        />
      )}
      {!hasImage && showImageSlot && <ImagePlaceholder isDark={isDark} />}

      {/* Content */}
      <Stack p={2} spacing={2} sx={{ flex: 1 }}>
        {/* Name + optional short description. It takes the spare height, so in a row of
            cards the facts and the button sit at the same level on every card. */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant={hasDescription ? "h6" : "body1"}
            fontWeight="bold"
          >
            {supply?.name}
          </Typography>

          {hasDescription && (
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                opacity: 0.75,
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {supply.shortDescription}
            </Typography>
          )}
        </Box>

        {/* Duration and price on the left, the button on the right, on one line; a card too
            narrow for all three moves the button under them. */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <AccessTimeIcon sx={{ fontSize: 16, opacity: 0.6 }} />
              <Typography variant="body1" fontWeight="bold" whiteSpace="nowrap">
                {supply?.duration} perc
              </Typography>
            </Stack>

            <Typography variant="body1" fontWeight="bold" whiteSpace="nowrap">
              {formatPrice(supply?.price)}
            </Typography>
          </Stack>

          <Box sx={{ ml: "auto" }}>
            <CustomButton text="Foglalás" onClick={handleClick} />
          </Box>
        </Stack>
      </Stack>
    </Card>
  );
};

export default SupplyItem;
